import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, Search, ShoppingBag, MessageCircle } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCart } from "@/context/CartContext";
import { BRAND, buildTelegramLink } from "@/config";
import { PromoBanner } from "@/components/PromoBanner";

const NAV = [
  { label: "Shop", to: "/shop", testid: "nav-link-shop" },
  { label: "About", to: "/about", testid: "nav-link-about" },
  { label: "Community", to: "/community", testid: "nav-link-community" },
];

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { count, setOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  const goToSection = (section) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${section}`);
      setTimeout(() => document.getElementById(section)?.scrollIntoView({ behavior: "smooth" }), 250);
    } else {
      document.getElementById(section)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/shop?search=${encodeURIComponent(query.trim())}`);
      setSearchOpen(false);
      setMobileOpen(false);
      setQuery("");
    }
  };

  const linkCls = transparent
    ? "text-sm text-white/90 hover:text-white transition-colors link-underline"
    : "text-sm text-slate-700 hover:text-slate-900 transition-colors link-underline";
  const iconCls = transparent ? "text-white hover:text-white/80" : "text-slate-700 hover:text-slate-900";

  const renderNavItem = (item, mobile = false) => {
    const cls = mobile ? "text-lg text-slate-800 hover:text-slate-900 transition-colors py-2" : linkCls;
    if (item.to) {
      return (
        <Link key={item.label} to={item.to} data-testid={item.testid} className={cls} onClick={() => setMobileOpen(false)}>
          {item.label}
        </Link>
      );
    }
    return (
      <button key={item.label} data-testid={item.testid} className={cls} onClick={() => goToSection(item.section)}>
        {item.label}
      </button>
    );
  };

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${transparent ? "bg-transparent" : "glass-strong border-b border-slate-200"}`}>
      <PromoBanner />
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8 h-16">
        <Link to="/" data-testid="nav-logo" className="group flex items-center">
          <span className={`font-wordmark text-lg sm:text-xl leading-none tracking-tight transition-colors ${transparent ? "text-white" : "text-slate-900"}`}>
            PEPSWORLD UK.
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => renderNavItem(item))}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          {searchOpen && (
            <form onSubmit={submitSearch} className="hidden md:block">
              <Input
                autoFocus
                data-testid="nav-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search research..."
                className="h-9 w-48 bg-white border-slate-200 text-sm text-slate-900"
              />
            </form>
          )}
          <Button variant="ghost" size="icon" data-testid="nav-search-toggle" onClick={() => setSearchOpen((v) => !v)} className={iconCls}>
            <Search className="h-5 w-5" />
          </Button>

          <Button variant="ghost" size="icon" data-testid="cart-trigger-button" onClick={() => setOpen(true)} className={`relative ${iconCls}`}>
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-blue-600 px-1 text-[10px] font-bold text-white">
                {count}
              </span>
            )}
          </Button>

          <a href={buildTelegramLink("Hello PepsWorld UK team, I have an enquiry about your research resources.")} target="_blank" rel="noreferrer" data-testid="nav-telegram" className="hidden sm:inline-flex">
            <Button className="h-9 rounded-full bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700">
              <MessageCircle className="mr-1.5 h-4 w-4" /> Message us
            </Button>
          </a>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger" className={`lg:hidden ${iconCls}`}>
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white border-slate-200 w-[85vw] sm:w-96">
              <SheetTitle className="font-display metallic-text text-xl">{BRAND.name}</SheetTitle>
              <SheetDescription className="sr-only">Site navigation and search</SheetDescription>
              <form onSubmit={submitSearch} className="mt-6">
                <Input
                  data-testid="mobile-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search research..."
                  className="bg-white border-slate-200 text-slate-900"
                />
              </form>
              <div className="mt-6 flex flex-col gap-1">
                {NAV.map((item) => renderNavItem(item, true))}
              </div>
              <a href={buildTelegramLink("Hello PepsWorld UK team, I have an enquiry.")} target="_blank" rel="noreferrer" className="mt-6 block">
                <Button className="w-full bg-blue-600 font-medium text-white hover:bg-blue-700" data-testid="mobile-telegram">
                  <MessageCircle className="mr-2 h-4 w-4" /> Message us
                </Button>
              </a>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
